import React, {PropsWithChildren} from 'react';
import {FormattedMessage} from 'react-intl';
import {Tooltip} from '@forecast-it/design-system';
import {HoverContainer, HoverLine, HoverLineHeader, HoverLineNumber} from './ProjectRetainerPeriodVisualComponents';
import {createFinancialCategoryTooltip as createTooltip} from '@/forecast-app/shared/components/tooltips/FinancialCategoryTooltip';
import {IntlShape} from 'react-intl';
import {type TooltipFormulaItem} from '@/components/TooltipFormulaRenderer';

type FormatMessage = IntlShape['formatMessage'];

const ActualPercentageBudgetUsedTooltip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'retainer.actual_percentage_budget_used'}),
		description: formatMessage({id: 'retainer.actual_percentage_budget_used.tooltip.description'}),
		details: [
			formatMessage({id: 'common.actual_billable_value_of_service'}),
			'/',
			formatMessage({id: 'retainer.period_price'}),
		],
	},
];
const ActualPercentageBudgetUsedWithRolloverTooltip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'retainer.actual_percentage_budget_used'}),
		description: formatMessage({id: 'retainer.actual_percentage_budget_used_with_rollover.tooltip.description'}),
		details: [
			formatMessage({id: 'common.actual_billable_value_of_service'}),
			'/',
			formatMessage({id: 'retainer.adjusted_price'}),
		],
	},
];

const ActualPercentageTargetUsedTooltip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'retainer.actual_percentage_target_used'}),
		description: formatMessage({id: 'retainer.actual_percentage_target_used.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_billable_hours'}), '/', formatMessage({id: 'retainer.period_target'})],
	},
];
const ActualPercentageTargetUsedWithRolloverTooltip = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'retainer.actual_percentage_target_used'}),
		description: formatMessage({id: 'retainer.actual_percentage_target_used_with_rollover.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_billable_hours'}), '/', formatMessage({id: 'retainer.adjusted_target'})],
	},
];

interface PeriodBreakdownTooltipProps {
	isFixedPrice: boolean;
	periodTarget: number;
	periodDifference: number;
	withSuffix: (value: number) => string;
}

const PeriodBreakdownTooltip: React.FC<PropsWithChildren<PeriodBreakdownTooltipProps>> = ({
	isFixedPrice,
	periodTarget,
	periodDifference,
	withSuffix,
	children,
}) => {
	return (
		<Tooltip
			interactable={false}
			width={'m'}
			content={
				<HoverContainer>
					<HoverLine>
						<HoverLineHeader>
							<FormattedMessage id={isFixedPrice ? 'retainer.period_price' : 'retainer.period_target'} />
						</HoverLineHeader>
						<HoverLineNumber large={!isFixedPrice}>{withSuffix(periodTarget)}</HoverLineNumber>
					</HoverLine>
					<HoverLine>
						<HoverLineHeader>
							<FormattedMessage id={'retainer.net_rollover'} />
						</HoverLineHeader>
						<HoverLineNumber large={!isFixedPrice}>{withSuffix(periodDifference)}</HoverLineNumber>
					</HoverLine>
					<HoverLine total>
						<HoverLineHeader>
							<FormattedMessage id={isFixedPrice ? 'retainer.adjusted_price' : 'retainer.adjusted_target'} />
						</HoverLineHeader>
						<HoverLineNumber large={!isFixedPrice}>{withSuffix(periodTarget + periodDifference)}</HoverLineNumber>
					</HoverLine>
				</HoverContainer>
			}
		>
			{children}
		</Tooltip>
	);
};

const NoTimeRegUpdatePermissionTooltip: React.FC = ({children}) => {
	return <Tooltip content={<FormattedMessage id="permission.no_time_reg_update_all_permission" />}>{children}</Tooltip>;
};

export const RetainerTooltips = {
	ActualPercentageBudgetUsedTooltip: createTooltip(ActualPercentageBudgetUsedTooltip),
	ActualPercentageBudgetUsedWithRolloverTooltip: createTooltip(ActualPercentageBudgetUsedWithRolloverTooltip),
	ActualPercentageTargetUsedTooltip: createTooltip(ActualPercentageTargetUsedTooltip),
	ActualPercentageTargetUsedWithRolloverTooltip: createTooltip(ActualPercentageTargetUsedWithRolloverTooltip),
	PeriodBreakdownTooltip,
	NoTimeRegUpdatePermissionTooltip,
};
